import { Redirect } from "@redotech/react-router-util/redirect";
import { orderFulfillmentAnalyticsRoutes } from "@redotech/redo-merchant-analytics-app/analytics/order-fulfillment/route";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { memo } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { outboundLabelRoutes, wmsRoutes } from "../../../order/route";
import { MerchantRouteNavigate } from "../../../redo-suite/merchant-routes";
import { carrierClaimsRoutes } from "../../../return/route";
import {
  type RoutePageComponent,
  SessionReplayRoutePage,
} from "./session-replay-route-page";
import { isOmsSessionReplayRoute } from "./shipping-route-matching";
import { warehouseRoutes } from "./warehouse-routes";

const SHIPPING_ROUTE_BASE = "/stores/:storeId/shipping";

const HIDE_PADDING_ROUTES: string[] = [
  "/batch-history",
  "/bulk-fulfillments",
  "/carrier-claims",
  "/consolidations",
  "/consolidations/*",
  "/eod-manifest",
  "/outbound-labels/*",
  "/outbound-labels/bad-warehouse-bins",
  "/wms/*",
  "/warehouse",
  "/warehouse/*",
  "/analytics/order-fulfillment",
];

function hidePadding(url: string): boolean {
  if (matchPath(`${SHIPPING_ROUTE_BASE}/outbound-labels/about`, url)) {
    return false;
  }
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${SHIPPING_ROUTE_BASE}${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/outbound-labels/personalized-slips/:personalizedSlipId",
  "/outbound-labels/personalized-inserts/:personalizedSlipId",
  "/outbound-labels/pick-sessions",
  "/outbound-labels/pick-sessions/:pickSessionId",
  "/outbound-labels/pick-sessions/:pickSessionId/tote-scanning",
  "/outbound-labels/pick-sessions/:pickSessionId/route",
  "/outbound-labels/create-pick-session",
  "/wms/work-orders/detail/:workOrderId",
  "/warehouse",
  "/warehouse/*",
];

function isFullscreenRoute(url: string): boolean {
  return FULLSCREEN_ROUTES.some(
    (route) => !!matchPath(`${SHIPPING_ROUTE_BASE}${route}`, url),
  );
}

function hideHeader(url: string): boolean {
  return isFullscreenRoute(url);
}

function hideNav(url: string): boolean {
  return isFullscreenRoute(url);
}

const ShippingRoutesShell = memo(function ShippingRoutesShell({
  Page,
}: {
  Page: RoutePageComponent;
}) {
  const location = useLocation();

  return (
    <SessionReplayRoutePage
      enabled={isOmsSessionReplayRoute(location.pathname)}
      hideHeader={hideHeader}
      hideNavbar={hideNav}
      hidePadding={hidePadding}
      Page={Page}
      productId="oms"
    />
  );
});

export const shippingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Shipping", breadcrumbNavigate: false },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <ShippingRoutesShell Page={Page} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="outbound-labels" /> },
      {
        // Legacy path: redirect to the canonical /outbound-labels/order-edits.
        path: "order-edits",
        element: (
          <MerchantRouteNavigate routeTo={(routes) => routes.orderEditsHref} />
        ),
      },
      {
        path: "analytics/order-fulfillment",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: orderFulfillmentAnalyticsRoutes,
      },
      { path: "carrier-claims", children: carrierClaimsRoutes },
      {
        path: "consolidations",
        handle: { breadcrumb: "Consolidations" },
        async lazy() {
          const { ConsolidationsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidations-page");
          return { Component: ConsolidationsPage };
        },
      },
      {
        path: "consolidations/:consolidationId",
        handle: { breadcrumb: "Consolidation" },
        async lazy() {
          const { ConsolidationDetailPage } =
            await import("@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-detail-page");
          return { Component: ConsolidationDetailPage };
        },
      },
      {
        path: "outbound-labels/packing-slips/*",
        element: (
          <Redirect
            match={/shipping\/outbound-labels\/packing-slips/}
            replaceValue="settings/oms/printing/packing-slips"
          />
        ),
      },
      { path: "outbound-labels", children: outboundLabelRoutes },
      { path: "wms", children: wmsRoutes },
      { path: "warehouse", children: warehouseRoutes },
    ],
  },
];
